import type { ComponentType } from "react";
import { BellIcon, PhoneIcon, ShieldIcon, UsersIcon } from "./components/icons";

export type SectionKey = "referrals" | "notifications" | "staff" | "recoveries";

export interface SectionDef {
  to: string;
  /** The rail's name for it, which is also its breadcrumb. */
  label: string;
  Icon: ComponentType;
  /**
   * Presentation only. The API enforces admin-only on these routes; hiding the
   * link just keeps RMs from walking into a guaranteed 403.
   */
  adminOnly: boolean;
}

/**
 * The console's destinations, named once for the rail, the tab bar, the page
 * titles and the breadcrumbs, so the word a trail leads back through is the
 * word on the rail beside it.
 */
export const SECTIONS = {
  referrals: { to: "/", label: "Referrals", Icon: UsersIcon, adminOnly: false },
  notifications: {
    to: "/notifications",
    label: "Notifications",
    Icon: BellIcon,
    adminOnly: false,
  },
  staff: { to: "/staff", label: "Staff", Icon: ShieldIcon, adminOnly: true },
  recoveries: {
    to: "/device-recoveries",
    label: "Recoveries",
    Icon: PhoneIcon,
    adminOnly: true,
  },
} as const satisfies Record<SectionKey, SectionDef>;

/** Rail order. */
export const SECTION_ORDER: readonly SectionKey[] = [
  "referrals",
  "notifications",
  "staff",
  "recoveries",
];

/**
 * Which section a member page was opened from, carried as `from` in router
 * state by the link that opened it. A member is reachable from the book and
 * from the recovery queue, and the trail (and the rail's highlight) should
 * lead back to whichever one it was. A bookmark or a refresh carries no
 * state, and belongs to the book.
 */
export function openedFrom(state: unknown): SectionKey {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from in SECTIONS ? (from as SectionKey) : "referrals";
}
